import { metresToFeet, msToKnots } from "../lib/format.js";
import "./PFD.css";

/**
 * PFD — a photo-real Airbus-style glass-cockpit Primary Flight Display.
 *
 * Self-contained SVG. Driven by live ADS-B telemetry.
 *
 *   <PFD aircraft={selectedAircraft} bankDeg={bank} />
 *
 * aircraft = {
 *   velocity (m/s), baroAltitude (m), heading (deg),
 *   verticalRate (m/s), onGround, callsign
 * } | null
 */

const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));
const degrees = (rad) => (rad * 180) / Math.PI;
const wrap360 = (d) => ((d % 360) + 360) % 360;

// --- Geometry constants (SVG user units, viewBox 500 x 460) --------------
const ADI_CX = 250; // attitude indicator centre x
const ADI_CY = 200; // attitude indicator centre y
const PITCH_PX = 6; // screen px per degree of pitch
const SPD_PX_PER_KT = 1.25; // airspeed tape scale
const ALT_PX_PER_FT = 0.16; // altitude tape scale
const HDG_PX_PER_DEG = 4.2; // heading tape scale

export default function PFD({ aircraft, bankDeg = 0 }) {
  // ---- Derive live values ------------------------------------------------
  const hasData = !!aircraft;

  const iasKt = hasData ? msToKnots(aircraft.velocity) : null;
  const altFt = hasData ? metresToFeet(aircraft.baroAltitude) : null;
  const vsFpm = hasData ? Math.round((aircraft.verticalRate ?? 0) * 196.85) : 0;
  const heading = hasData ? aircraft.heading ?? 0 : 0;

  const velocity = hasData ? aircraft.velocity ?? 0 : 0;
  const vr = hasData ? aircraft.verticalRate ?? 0 : 0;
  const pitchDeg =
    velocity > 10
      ? clamp(degrees(Math.asin(clamp(vr / velocity, -1, 1))), -15, 15)
      : 0;

  const roll = bankDeg || 0;
  const callsign =
    hasData && aircraft.callsign && aircraft.callsign.trim()
      ? aircraft.callsign.trim()
      : "";

  const ariaLabel = hasData
    ? `Primary flight display. Speed ${iasKt ?? "unknown"} knots, ` +
      `Altitude ${altFt ?? "unknown"} feet, Heading ${Math.round(heading)} degrees.`
    : "Primary flight display standby. No data.";

  // ---- No-data standby screen -------------------------------------------
  if (!hasData) {
    return (
      <div className="pfd" role="img" aria-label={ariaLabel}>
        <svg
          className="pfd__svg pfd__svg--stby"
          viewBox="0 0 500 460"
          preserveAspectRatio="xMidYMid meet"
        >
          <rect x="0" y="0" width="500" height="460" fill="#05070a" />
          <rect
            x="6"
            y="6"
            width="488"
            height="448"
            rx="14"
            fill="none"
            stroke="#1a2530"
            strokeWidth="2"
          />
          <text
            x="250"
            y="215"
            className="pfd__stby-main"
            textAnchor="middle"
          >
            STBY
          </text>
          <text
            x="250"
            y="250"
            className="pfd__stby-sub"
            textAnchor="middle"
          >
            NO DATA
          </text>
          <rect
            x="0"
            y="0"
            width="500"
            height="460"
            fill="url(#pfdVignetteStby)"
            pointerEvents="none"
          />
          <defs>
            <radialGradient id="pfdVignetteStby" cx="50%" cy="50%" r="72%">
              <stop offset="55%" stopColor="#000" stopOpacity="0" />
              <stop offset="100%" stopColor="#000" stopOpacity="0.7" />
            </radialGradient>
          </defs>
        </svg>
      </div>
    );
  }

  // ---- Pitch ladder marks ------------------------------------------------
  const pitchMarks = [];
  for (let v = -15; v <= 15; v += 5) {
    if (v === 0) continue;
    const y = ADI_CY - v * PITCH_PX;
    const half = Math.abs(v) === 5 ? 18 : Math.abs(v) === 10 ? 30 : 42;
    pitchMarks.push({ v, y, half });
  }

  // ---- Bank scale ticks --------------------------------------------------
  const bankTicks = [-45, -30, -20, -10, 10, 20, 30, 45];
  const bankArcR = 148;

  // ---- Airspeed tape ticks ----------------------------------------------
  const spdTicks = [];
  const spdBase = Math.round((iasKt ?? 0) / 10) * 10;
  for (let s = spdBase - 60; s <= spdBase + 60; s += 10) {
    if (s < 0) continue;
    const y = ADI_CY - (s - (iasKt ?? 0)) * SPD_PX_PER_KT;
    spdTicks.push({ s, y });
  }

  // ---- Altitude tape ticks ----------------------------------------------
  const altTicks = [];
  const altBase = Math.round((altFt ?? 0) / 100) * 100;
  for (let a = altBase - 500; a <= altBase + 500; a += 100) {
    const y = ADI_CY - (a - (altFt ?? 0)) * ALT_PX_PER_FT;
    altTicks.push({ a, y });
  }

  // ---- Heading tape ticks -----------------------------------------------
  const hdgTicks = [];
  const hdgBase = Math.round(heading / 10) * 10;
  for (let h = hdgBase - 50; h <= hdgBase + 50; h += 10) {
    const x = ADI_CX + (h - heading) * HDG_PX_PER_DEG;
    const label = wrap360(h);
    hdgTicks.push({ x, label, major: label % 30 === 0 });
  }

  // ---- V/S pointer -------------------------------------------------------
  const vsClamped = clamp(vsFpm / 2000, -1, 1);
  const vsPointerY = ADI_CY - vsClamped * 130;
  const vsPositive = vsFpm >= 0;

  return (
    <div className="pfd" role="img" aria-label={ariaLabel}>
      <svg
        className="pfd__svg"
        viewBox="0 0 500 460"
        preserveAspectRatio="xMidYMid meet"
      >
        <defs>
          <linearGradient id="pfdSky" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#1f78d6" />
            <stop offset="55%" stopColor="#2f9be6" />
            <stop offset="100%" stopColor="#8fcdf2" />
          </linearGradient>
          <linearGradient id="pfdGround" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#b98a3d" />
            <stop offset="45%" stopColor="#8a5f27" />
            <stop offset="100%" stopColor="#5c3d16" />
          </linearGradient>
          <linearGradient id="pfdTape" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#0a0f14" stopOpacity="0.94" />
            <stop offset="100%" stopColor="#0a0f14" stopOpacity="0.72" />
          </linearGradient>
          <radialGradient id="pfdVignette" cx="50%" cy="50%" r="72%">
            <stop offset="58%" stopColor="#000" stopOpacity="0" />
            <stop offset="100%" stopColor="#000" stopOpacity="0.6" />
          </radialGradient>
          <clipPath id="pfdAdiClip">
            <rect x="110" y="34" width="280" height="332" rx="16" />
          </clipPath>
        </defs>

        {/* Screen background */}
        <rect x="0" y="0" width="500" height="460" fill="#05070a" />

        {/* ================= ATTITUDE INDICATOR ================= */}
        <g clipPath="url(#pfdAdiClip)">
          {/* rotate whole horizon by -bank around centre */}
          <g transform={`rotate(${-roll} ${ADI_CX} ${ADI_CY})`}>
            {/* pitch translation */}
            <g transform={`translate(0 ${pitchDeg * PITCH_PX})`}>
              <rect
                x="-400"
                y="-500"
                width="1300"
                height={500 + ADI_CY}
                fill="url(#pfdSky)"
              />
              <rect
                x="-400"
                y={ADI_CY}
                width="1300"
                height="800"
                fill="url(#pfdGround)"
              />
              {/* horizon line */}
              <line
                x1="-400"
                y1={ADI_CY}
                x2="900"
                y2={ADI_CY}
                stroke="#ffffff"
                strokeWidth="2.5"
              />
              {/* pitch ladder */}
              {pitchMarks.map(({ v, y, half }) => (
                <g key={v} className="pfd__ladder">
                  <line
                    x1={ADI_CX - half}
                    y1={y}
                    x2={ADI_CX + half}
                    y2={y}
                    stroke="#e8f0f5"
                    strokeWidth="1.6"
                  />
                  {Math.abs(v) >= 10 && (
                    <>
                      <text
                        x={ADI_CX - half - 6}
                        y={y + 4}
                        className="pfd__ladder-num"
                        textAnchor="end"
                      >
                        {Math.abs(v)}
                      </text>
                      <text
                        x={ADI_CX + half + 6}
                        y={y + 4}
                        className="pfd__ladder-num"
                        textAnchor="start"
                      >
                        {Math.abs(v)}
                      </text>
                    </>
                  )}
                </g>
              ))}
            </g>
          </g>
        </g>

        {/* ADI frame */}
        <rect
          x="110"
          y="34"
          width="280"
          height="332"
          rx="16"
          fill="none"
          stroke="#0d1319"
          strokeWidth="6"
        />

        {/* ---- Bank scale arc (fixed) ---- */}
        <g className="pfd__bankscale">
          {bankTicks.map((t) => {
            const ang = (t - 90) * (Math.PI / 180);
            const x1 = ADI_CX + bankArcR * Math.cos(ang);
            const y1 = ADI_CY + bankArcR * Math.sin(ang);
            const len = Math.abs(t) === 45 ? 12 : Math.abs(t) === 30 ? 12 : 8;
            const x2 = ADI_CX + (bankArcR - len) * Math.cos(ang);
            const y2 = ADI_CY + (bankArcR - len) * Math.sin(ang);
            return (
              <line
                key={t}
                x1={x1}
                y1={y1}
                x2={x2}
                y2={y2}
                stroke="#e8f0f5"
                strokeWidth="1.8"
              />
            );
          })}
          {/* zero-bank reference triangle at top */}
          <polygon
            points={`${ADI_CX},${ADI_CY - bankArcR - 2} ${ADI_CX - 7},${
              ADI_CY - bankArcR - 15
            } ${ADI_CX + 7},${ADI_CY - bankArcR - 15}`}
            fill="#e8f0f5"
          />
        </g>

        {/* ---- Roll pointer (rotates with bank) ---- */}
        <g transform={`rotate(${-roll} ${ADI_CX} ${ADI_CY})`}>
          <polygon
            points={`${ADI_CX},${ADI_CY - bankArcR + 4} ${ADI_CX - 9},${
              ADI_CY - bankArcR + 19
            } ${ADI_CX + 9},${ADI_CY - bankArcR + 19}`}
            fill="#ffb020"
            stroke="#5a3a00"
            strokeWidth="0.6"
          />
        </g>

        {/* ---- Fixed aircraft reference symbol (does NOT move) ---- */}
        <g className="pfd__ref">
          <line
            x1={ADI_CX - 70}
            y1={ADI_CY}
            x2={ADI_CX - 26}
            y2={ADI_CY}
            stroke="#ffb020"
            strokeWidth="5"
          />
          <line
            x1={ADI_CX - 26}
            y1={ADI_CY}
            x2={ADI_CX - 26}
            y2={ADI_CY + 10}
            stroke="#ffb020"
            strokeWidth="5"
          />
          <line
            x1={ADI_CX + 70}
            y1={ADI_CY}
            x2={ADI_CX + 26}
            y2={ADI_CY}
            stroke="#ffb020"
            strokeWidth="5"
          />
          <line
            x1={ADI_CX + 26}
            y1={ADI_CY}
            x2={ADI_CX + 26}
            y2={ADI_CY + 10}
            stroke="#ffb020"
            strokeWidth="5"
          />
          <circle cx={ADI_CX} cy={ADI_CY} r="3" fill="#ffb020" />
        </g>

        {/* ================= AIRSPEED TAPE ================= */}
        <g>
          <rect x="30" y="34" width="70" height="332" rx="6" fill="url(#pfdTape)" />
          <g clipPath="url(#pfdSpdClip)">
            {spdTicks.map(({ s, y }) => (
              <g key={s}>
                <line
                  x1="82"
                  y1={y}
                  x2="100"
                  y2={y}
                  stroke="#e8f0f5"
                  strokeWidth="1.4"
                />
                <text x="76" y={y + 4} className="pfd__tape-num" textAnchor="end">
                  {s}
                </text>
              </g>
            ))}
          </g>
          {/* current value box */}
          <polygon
            points={`30,${ADI_CY - 15} 88,${ADI_CY - 15} 100,${ADI_CY} 88,${
              ADI_CY + 15
            } 30,${ADI_CY + 15}`}
            fill="#05070a"
            stroke="#12e29a"
            strokeWidth="1.6"
          />
          <text
            x="80"
            y={ADI_CY + 6}
            className="pfd__readout"
            textAnchor="end"
          >
            {iasKt ?? "---"}
          </text>
          <text x="65" y="26" className="pfd__tape-label" textAnchor="middle">
            IAS kt
          </text>
        </g>

        {/* ================= ALTITUDE TAPE ================= */}
        <g>
          <rect x="398" y="34" width="72" height="332" rx="6" fill="url(#pfdTape)" />
          <g clipPath="url(#pfdAltClip)">
            {altTicks.map(({ a, y }) => (
              <g key={a}>
                <line
                  x1="398"
                  y1={y}
                  x2="416"
                  y2={y}
                  stroke="#e8f0f5"
                  strokeWidth="1.4"
                />
                <text
                  x="422"
                  y={y + 4}
                  className="pfd__tape-num"
                  textAnchor="start"
                >
                  {a}
                </text>
              </g>
            ))}
          </g>
          {/* current value box */}
          <polygon
            points={`470,${ADI_CY - 15} 412,${ADI_CY - 15} 400,${ADI_CY} 412,${
              ADI_CY + 15
            } 470,${ADI_CY + 15}`}
            fill="#05070a"
            stroke="#12e29a"
            strokeWidth="1.6"
          />
          <text
            x="420"
            y={ADI_CY + 6}
            className="pfd__readout"
            textAnchor="start"
          >
            {altFt ?? "-----"}
          </text>
          <text x="434" y="26" className="pfd__tape-label" textAnchor="middle">
            ALT ft
          </text>
        </g>

        {/* ================= VERTICAL SPEED SCALE ================= */}
        <g>
          <rect x="474" y="60" width="22" height="280" rx="5" fill="url(#pfdTape)" />
          <line
            x1="474"
            y1={ADI_CY}
            x2="496"
            y2={ADI_CY}
            stroke="#4a5560"
            strokeWidth="1"
          />
          {[2000, 1000, -1000, -2000].map((v) => {
            const y = ADI_CY - clamp(v / 2000, -1, 1) * 130;
            return (
              <g key={v}>
                <line
                  x1="474"
                  y1={y}
                  x2="480"
                  y2={y}
                  stroke="#e8f0f5"
                  strokeWidth="1.2"
                />
                <text x="485" y={y + 3} className="pfd__vs-num" textAnchor="start">
                  {Math.abs(v / 1000)}
                </text>
              </g>
            );
          })}
          {/* pointer */}
          <polygon
            points={`474,${vsPointerY} 480,${vsPointerY - 4} 480,${
              vsPointerY + 4
            }`}
            fill={vsPositive ? "#12e29a" : "#ffb020"}
          />
          <line
            x1="474"
            y1={vsPointerY}
            x2="480"
            y2={vsPointerY}
            stroke={vsPositive ? "#12e29a" : "#ffb020"}
            strokeWidth="2"
          />
          <text x="485" y="55" className="pfd__tape-label" textAnchor="middle">
            V/S
          </text>
          <text
            x="485"
            y="352"
            className="pfd__vs-val"
            textAnchor="middle"
            fill={vsPositive ? "#12e29a" : "#ffb020"}
          >
            {vsFpm > 0 ? `+${vsFpm}` : vsFpm}
          </text>
        </g>

        {/* ================= HEADING STRIP ================= */}
        <g>
          <rect x="110" y="392" width="280" height="46" rx="8" fill="url(#pfdTape)" />
          <g clipPath="url(#pfdHdgClip)">
            {hdgTicks.map(({ x, label, major }) => (
              <g key={label}>
                <line
                  x1={x}
                  y1="392"
                  x2={x}
                  y2={major ? 404 : 400}
                  stroke="#e8f0f5"
                  strokeWidth={major ? 1.6 : 1.1}
                />
                {major && (
                  <text
                    x={x}
                    y="422"
                    className="pfd__hdg-num"
                    textAnchor="middle"
                  >
                    {String(label).padStart(3, "0")}
                  </text>
                )}
              </g>
            ))}
          </g>
          {/* magenta index */}
          <polygon
            points={`${ADI_CX},392 ${ADI_CX - 7},380 ${ADI_CX + 7},380`}
            fill="#ff4fd8"
          />
          {/* current heading box */}
          <rect
            x={ADI_CX - 26}
            y="380"
            width="52"
            height="22"
            rx="3"
            fill="#05070a"
            stroke="#12e29a"
            strokeWidth="1.4"
          />
          <text x={ADI_CX} y="396" className="pfd__hdg-cur" textAnchor="middle">
            {String(Math.round(wrap360(heading))).padStart(3, "0")}
          </text>
        </g>

        {/* Callsign tag */}
        {callsign && (
          <text x="118" y="452" className="pfd__callsign" textAnchor="start">
            {callsign}
          </text>
        )}
        {aircraft.onGround && (
          <text x="382" y="452" className="pfd__ground" textAnchor="end">
            ON GND
          </text>
        )}

        {/* Clip paths for tapes */}
        <defs>
          <clipPath id="pfdSpdClip">
            <rect x="30" y="34" width="70" height="332" rx="6" />
          </clipPath>
          <clipPath id="pfdAltClip">
            <rect x="398" y="34" width="72" height="332" rx="6" />
          </clipPath>
          <clipPath id="pfdHdgClip">
            <rect x="110" y="392" width="280" height="46" rx="8" />
          </clipPath>
        </defs>

        {/* Vignette */}
        <rect
          x="0"
          y="0"
          width="500"
          height="460"
          fill="url(#pfdVignette)"
          pointerEvents="none"
        />
        {/* bezel */}
        <rect
          x="3"
          y="3"
          width="494"
          height="454"
          rx="16"
          fill="none"
          stroke="#12181f"
          strokeWidth="6"
        />
      </svg>
    </div>
  );
}
